import React from 'react';

import { IDefaultLayout } from '@/interfaces/Layouts';
import { Helmet } from 'react-helmet';
import FontStyle from '@/components/FontStyle';
import { Head } from '@/components/Head';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ProcessImageWithOpacity from '@/lib/ProcessImageWithOpacity';
import { isProductNotToBeListed } from '@/lib/helpers';
import FloatingWhatsappButton from '@/components/FloatingWhatsappButton';
import AgeConfirmation from '@/components/AgeConfirmation';

import './style.scss';

export const DefaultLayout = (defaultLayoutData: IDefaultLayout) => {
  const newDefaultLayoutData = { ...defaultLayoutData };
  const { title, backgroundHeader, templateData } = newDefaultLayoutData;
  const { general, home, integrations, footer, ageConfirmation } = templateData;
  const { widgetTarget, categoriesWithProducts, robotsNotIndexAndNoFollow } =
    newDefaultLayoutData.pageContext;
  const { publicApiToken, products, locale } =
    newDefaultLayoutData.pageContext.appData;
  const { notListedProducts } = home;
  const { whatsapp = null, whatsappButton, whatsappMessage } = footer;

  if (!('backgroundImage' in newDefaultLayoutData))
    newDefaultLayoutData.backgroundImage = undefined;

  newDefaultLayoutData.backgroundImage = ProcessImageWithOpacity({
    url: home.homeBgImageUrl,
    opacity: home.opacity,
  });

  newDefaultLayoutData.pageContext.categoriesWithProducts =
    categoriesWithProducts.map((category) => ({
      ...category,
      products: notListedProducts
        ? category.products.filter(
            (product) => !isProductNotToBeListed(product.id, notListedProducts)
          )
        : category.products,
    }));

  newDefaultLayoutData.pageContext.appData.products = notListedProducts
    ? products.filter(
        (product) => !isProductNotToBeListed(product.id, notListedProducts)
      )
    : products;

  const institutionalPageBg =
    backgroundHeader &&
    ProcessImageWithOpacity({
      url: backgroundHeader.url!,
      opacity: backgroundHeader.opacity!,
    });

  const scriptGtag = integrations.gtag && (
    <script>
      {`
                (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${integrations.gtag}');`}
    </script>
  );
  const noScriptGtag = integrations.gtag && (
    <noscript
      dangerouslySetInnerHTML={{
        __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=${integrations.gtag}" height="0" width="0" style="display:none;visibility:hidden"></iframe>
            `,
      }}
    />
  );

  return (
    <>
      <Head
        {...{
          general,
          publicApiToken,
          title,
          widgetTarget,
          integrations,
          actionColor: newDefaultLayoutData.templateData.general.colors.cta,
        }}
      />
      <Helmet htmlAttributes={{ lang: newDefaultLayoutData.pageContext.lang }}>
        <title>
          {title ? `${title} - ` : ''}
          {general.longName}{' '}
        </title>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta charSet="utf-8" />
        <meta name="description" content={general.description} />
        <meta property="og:image" content={home.homeBgImageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={general.longName} />
        <meta name="generator" content="Planne" />
        <meta property="og:title" content={general.longName} />
        <meta property="og:description" content={general.description} />

        <meta property="twitter:card" content="summary" />
        <meta property="twitter:title" content={general.longName} />
        <meta property="twitter:image" content={home.homeBgImageUrl} />
        <meta property="twitter:description" content={general.description} />

        {robotsNotIndexAndNoFollow && (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <link rel="icon" type="image" href={general.favicon}></link>
        <script src="/scripts/index.js" type="module"></script>
        {scriptGtag}
        <style>{`

                    .highlight-container{
                        background-image: ${newDefaultLayoutData.backgroundImage};
                    }

                    .institutional-page-header{
                        background-image: ${institutionalPageBg};
                    }

                `}</style>
      </Helmet>
      {noScriptGtag}
      <FontStyle font={general.fontFamily} />

      <Header {...newDefaultLayoutData} />
      <div className="page-content default">
        {newDefaultLayoutData.children}
      </div>
      {whatsappButton && whatsapp ? (
        <FloatingWhatsappButton whatsapp={whatsapp} message={whatsappMessage} />
      ) : null}
      <Footer {...newDefaultLayoutData} />

      {ageConfirmation ? (
        <AgeConfirmation
          ageConfirmation={ageConfirmation}
          defaultValues={locale.age_confirmation}
          altLogo={general.longName}
          logo={general.logoUrl}
        />
      ) : null}
    </>
  );
};
